import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { SiteContent } from '../types'
import { fetchContent } from '../lib/api'
import { INITIAL_CONTENT } from './initial'

interface ContentState {
  content: SiteContent
  /** True once the CMS response has been applied. */
  loaded: boolean
}

const ContentCtx = createContext<ContentState>({ content: INITIAL_CONTENT, loaded: false })

/**
 * Loads every editable string and image from the CMS (via the gateway) once, at
 * mount, and hands it to the whole tree.
 *
 * `INITIAL_CONTENT` is only a first-paint placeholder so the page is never blank
 * while the request is in flight. As soon as the CMS answers, its values are used
 * wholesale — including empty ones — so clearing a field in the CMS clears it on
 * the site. Only a failed request leaves the placeholder in place.
 */
export function ContentProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ContentState>({ content: INITIAL_CONTENT, loaded: false })

  useEffect(() => {
    const ctrl = new AbortController()
    fetchContent(ctrl.signal)
      .then((data) => {
        if (!data) return
        setState({
          content: {
            home: data.home ?? {},
            settings: data.settings ?? {},
            ministries: Array.isArray(data.ministries) ? data.ministries : [],
          },
          loaded: true,
        })
      })
      .catch(() => {
        /* request failed or aborted — keep the placeholder */
      })
    return () => ctrl.abort()
  }, [])

  return <ContentCtx.Provider value={state}>{children}</ContentCtx.Provider>
}

/** All CMS content. Components read exactly the fields they render. */
export function useContent(): SiteContent {
  return useContext(ContentCtx).content
}

/** True once CMS content has replaced the first-paint placeholder. */
export function useContentLoaded(): boolean {
  return useContext(ContentCtx).loaded
}
